import { Injectable } from '@angular/core';
import { MetadataService } from '@carm/_core/metadata/services';
import { IKeyValue } from '@carm/_utils/models';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CadMetadataService } from '@carm/_core/declarations/services/cad-metadata.service';
import { LevelOfAdjustment } from '@carm/_core/declarations/models/common';
import { EditFieldMetadataTypeEnum, EditFieldBase, goodEditFields, invoiceEditFields, generalDetailsEditFields } from '../models';

@Injectable()
export class EditFieldService {
  private metadataDictionary: { [id: string]: IKeyValue[] } = {};

  constructor(
    private cadMetadataService: CadMetadataService,
    private metadataService: MetadataService,
    private translateService: TranslateService
  ) {}

  loadMetadata(
    metadataType: EditFieldMetadataTypeEnum
  ): Observable<IKeyValue[]> {
    if (this.metadataDictionary[metadataType]) {
      return of(this.metadataDictionary[metadataType]);
    }

    switch (metadataType) {
      case EditFieldMetadataTypeEnum.CountryList:
        return this.metadataService.getCountryList();
      case EditFieldMetadataTypeEnum.UnitOfMeasure:
        return this.cadMetadataService.getUnitOfMeasureCodes(''); // TODO: pass classificationCode. don't know how to handle this one yet
      case EditFieldMetadataTypeEnum.DestinationProvince:
        return this.cadMetadataService.canadianProvinces;
      case EditFieldMetadataTypeEnum.TariffTreatment:
        return this.cadMetadataService.getTariffTreatments(
          null,
          null,
          null,
          null
        );
      case EditFieldMetadataTypeEnum.TariffCode:
        return this.cadMetadataService.tariffCodes;
      case EditFieldMetadataTypeEnum.TimeLimitType:
        return this.cadMetadataService.timeLimitTypes;
      case EditFieldMetadataTypeEnum.ExciseTaxCode:
        return this.cadMetadataService.getExciseCodes('', null); // TODO: pass classificationCode. don't know how to handle this one yet
      case EditFieldMetadataTypeEnum.GSTCode:
        return this.cadMetadataService.gstCodes;
      case EditFieldMetadataTypeEnum.USPortOfExit:
        return this.cadMetadataService.usPorts;
      case EditFieldMetadataTypeEnum.ModeOfTransport:
        return this.cadMetadataService.modesOfTransport;
      case EditFieldMetadataTypeEnum.PortOfUnladingRegion:
        return this.cadMetadataService.portOfLadingRegion;
      case EditFieldMetadataTypeEnum.PortOfUnlading:
        return this.cadMetadataService.getPortOfLading(''); // TODO: Point to a new API
      case EditFieldMetadataTypeEnum.CurrencyCode:
        return this.cadMetadataService.currencies.pipe(
          tap((kvArr: IKeyValue[]) =>
            kvArr.map(kv => (kv.value = `${kv.key} - ${kv.value}`))
          )
        );
      default:
        return of([]);
    }
  }

  cacheMetadata(metadataType: EditFieldMetadataTypeEnum, data: IKeyValue[]) {
    this.metadataDictionary[metadataType] = data;
  }

  getEditFields(
    levelOfAdjustment: LevelOfAdjustment
  ): Observable<EditFieldBase<any>[]> {
    let editFields: EditFieldBase<any>[] = [];

    switch (levelOfAdjustment) {
      case LevelOfAdjustment.GOODS:
        editFields = [...goodEditFields];
        break;
      case LevelOfAdjustment.INVOICE:
        editFields = [...invoiceEditFields];
        break;
      case LevelOfAdjustment.DETAILS_WAREHOUSE:
        editFields = [...generalDetailsEditFields];
        break;
    }

    editFields = editFields.map(ef => {
      ef.value = this.translateService.instant(ef.i18nText);
      return ef;
    });

    return of(editFields);
  }
}
